/**
 * Trailer type vocabulary shared with EAPI, in both directions:
 *
 *  - inbound: customers report arrivals to
 *    /v1/trailer-arrival/locations/{location_id}/trailers/check-in with
 *    `equipment.type` (supabase/functions/trailer-arrival)
 *  - outbound: AYMS gate events to /v1/ayms/events carry `trailer_type`
 *    (src/lib/ayms-event.ts, ASE-20496)
 *
 * ATMS stores trailer types in `trailer_types` keyed by `kind` + `length_ft`
 * (slug `<length>_<kind>`), and its `kind` vocabulary differs from EAPI's in
 * one place: ATMS says "intermodal" for what EAPI calls "container". This
 * module is the single place that difference lives. Both the browser and
 * the Deno edge functions import it directly, so keep it dependency-free.
 */

/** The values EAPI accepts and emits. */
export const EAPI_TRAILER_TYPES = ["dry_van", "reefer", "container", "roughneck"] as const;
export type EapiTrailerType = (typeof EAPI_TRAILER_TYPES)[number];

/** `trailer_types.kind` values ATMS seeds. */
export const TRAILER_KINDS = ["dry_van", "reefer", "intermodal", "roughneck"] as const;
export type TrailerKind = (typeof TRAILER_KINDS)[number];

/** Slug a new trailer gets when the sender's type/length is unknown or unseeded. */
export const DEFAULT_TRAILER_TYPE_SLUG = "48_dry_van";

const KIND_TO_EAPI: Record<TrailerKind, EapiTrailerType> = {
  dry_van: "dry_van",
  reefer: "reefer",
  intermodal: "container",
  roughneck: "roughneck",
};

const EAPI_TO_KIND: Record<EapiTrailerType, TrailerKind> = {
  dry_van: "dry_van",
  reefer: "reefer",
  container: "intermodal",
  roughneck: "roughneck",
};

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function isEapiTrailerType(value: unknown): value is EapiTrailerType {
  return typeof value === "string" && (EAPI_TRAILER_TYPES as readonly string[]).includes(value);
}

export function isTrailerKind(value: unknown): value is TrailerKind {
  return typeof value === "string" && (TRAILER_KINDS as readonly string[]).includes(value);
}

/**
 * Lenient parse of a sender-supplied type ("Dry Van", " reefer ", "Container").
 * ATMS's own kind names are accepted too so "intermodal" reads as container.
 * Unknown values are undefined, never an error: inbound callers fall back to
 * DEFAULT_TRAILER_TYPE_SLUG and outbound callers omit the field.
 */
export function parseEapiTrailerType(value: string | null | undefined): EapiTrailerType | undefined {
  const v = normalize(value);
  if (isEapiTrailerType(v)) return v;
  if (isTrailerKind(v)) return KIND_TO_EAPI[v];
  return undefined;
}

/** ATMS `trailer_types.kind` → EAPI type for outbound payloads. */
export function eapiTrailerTypeFromKind(kind: string | null | undefined): EapiTrailerType | undefined {
  const k = normalize(kind);
  return isTrailerKind(k) ? KIND_TO_EAPI[k] : undefined;
}

/** EAPI type (or any sender spelling) → ATMS `trailer_types.kind` for inbound writes. */
export function trailerKindFromEapiType(value: string | null | undefined): TrailerKind | undefined {
  const t = parseEapiTrailerType(value);
  return t ? EAPI_TO_KIND[t] : undefined;
}

/**
 * `trailer_types.slug` for a sender-supplied type + length, e.g.
 * ("container", 53) → "53_intermodal". Null when either is missing or the
 * type is unknown; the caller then uses DEFAULT_TRAILER_TYPE_SLUG.
 */
export function trailerTypeSlug(type: string | null | undefined, lengthFt: number | null | undefined): string | null {
  const kind = trailerKindFromEapiType(type);
  if (!kind || !lengthFt) return null;
  return `${lengthFt}_${kind}`;
}
